import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { toast } from 'react-toastify';
import { IconDownload, IconCopy, IconCheck } from '@tabler/icons-react';
import useTranslation from '@/hooks/useTranslation';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  useMutationDisableTwoFactor,
  useMutationEnableTwoFactor,
  useMutationGenerateTwoFactorSecret,
} from '@/reactQuery/post';
import { useUser } from '@/hooks/useUser';
import { TwoFactorSetupStep } from '@/enums';
import { QrCodeData } from '@/types';
import ToastMessage from '../toast-message/ToastMessage';

const setupValidationSchema = Yup.object().shape({
  code: Yup.string()
    .required('codeIsRequired')
    .matches(/^\d{6}$/, 'invalidCode'),
});

const disableValidationSchema = Yup.object().shape({
  password: Yup.string().required('passwordIsRequired'),
});

function TwoFactorAuthForm() {
  const { user, refetch: refetchUser } = useUser();
  const { lang } = useTranslation();
  const { currentTheme } = useTheme();
  const [setupStep, setSetupStep] = useState<TwoFactorSetupStep>(TwoFactorSetupStep.INITIAL);
  const [qrCodeData, setQrCodeData] = useState<QrCodeData | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const { mutateAsync: generateSecretMutation, isPending: generateSecretMutationIsPenging } =
    useMutationGenerateTwoFactorSecret();
  const { mutateAsync: enableTwoFactorMutation, isPending: enableTwoFactorMutationIsPending } =
    useMutationEnableTwoFactor();
  const { mutateAsync: disableTwoFactorMutation, isPending: disableTwoFactorMutationIsPending } =
    useMutationDisableTwoFactor();

  // Setup form
  const {
    register: setupRegister,
    handleSubmit: setupHandleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset: setupReset,
  } = useForm({
    defaultValues: { code: '' },
    resolver: yupResolver(setupValidationSchema),
    mode: 'onChange',
  });

  // Disable form
  const {
    register: disableRegister,
    handleSubmit: disableHandleSubmit,
    formState: { errors: disableErrors, isSubmitting: disableIsSubmitting },
    reset: disableReset,
  } = useForm({
    defaultValues: { password: '' },
    resolver: yupResolver(disableValidationSchema),
    mode: 'onChange',
  });

  // Start 2FA setup
  const startSetup = async () => {
    try {
      setError('');
      const { data } = await generateSecretMutation();
      setQrCodeData(data);
      setSetupStep(TwoFactorSetupStep.QR_CODE);
    } catch (err) {
      toast(<ToastMessage title="error" description="somethingWentWrong" />);
    }
  };

  // Verify code and enable 2FA
  const verifyAndEnable = async (data: { code: string }) => {
    if (!qrCodeData) return;

    try {
      setError('');
      const { data: response } = await enableTwoFactorMutation({
        secret: qrCodeData.secret,
        token: data.code,
      });
      setBackupCodes(response.backupCodes);
      setSetupStep(TwoFactorSetupStep.BACKUP_CODES);
      await refetchUser();

      toast.success(
        <ToastMessage title="twoFactorSetupSuccess" description="backupCodesExplainer" />,
      );
    } catch (err: any) {
      setError(err.data.message || lang.get('msg.invalidCode'));
    }
  };

  // Disable 2FA
  const disableTwoFactor = async (data: { password: string }) => {
    try {
      setPasswordError('');

      await disableTwoFactorMutation({
        password: data.password,
      });

      await refetchUser();
      disableReset();
      setupReset();
      toast.success(lang.get('msg.twoFactorDisabledSuccess'));
    } catch (err: any) {
      setPasswordError(err.data.message);
    }
  };

  // Download backup codes
  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tellet-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Copy backup codes to clipboard
  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      toast.success(lang.get('msg.backupCopiesSuccess'));
    });
  };

  return (
    <>
      <h2 className="text-lg font-semibold mt-10">{lang.get('msg.twoFactorAuthentication')}</h2>
      <p
        style={{ color: currentTheme?.['secondary-text'] }}
        className="mt-1 mb-6 text-sm text-light-gray"
      >
        {lang.get('msg.twoFactorAuthenticationDescription')}
      </p>
      <hr className="mb-6" />

      {/* Initial State */}
      {setupStep === TwoFactorSetupStep.INITIAL && (
        <div className="mt-5">
          <div className="p-4 border rounded-md mb-5">
            <p className="font-semibold">
              {user?.twoFactorEnabled
                ? lang.get('msg.twoFactorAuthenticationEnabled')
                : lang.get('msg.twoFactorAuthenticationDisabled')}
            </p>
          </div>

          {user?.twoFactorEnabled ? (
            // Disable 2FA Form
            <form onSubmit={disableHandleSubmit(disableTwoFactor)} className="max-w-lg">
              <div className="flex flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative mb-1">
                <label style={{ color: currentTheme?.['primary-text'] }}>
                  {lang.get('msg.enterPassword')}
                </label>
                <Input
                  {...disableRegister('password', {
                    onChange: () => {
                      if (passwordError) setPasswordError('');
                    },
                  })}
                  type="password"
                  className={disableErrors.password ? 'border-red-600' : ''}
                />
                {disableErrors.password?.message && (
                  <p className="text-sm text-red-600">
                    {lang.get(`msg.${disableErrors.password?.message}`)}
                  </p>
                )}
              </div>

              {passwordError && (
                <p className="text-sm text-red-600 mb-4 font-semibold  first-letter:uppercase">
                  {passwordError}
                </p>
              )}

              <Button
                className="mt-4"
                variant="destructive"
                type="submit"
                disabled={disableIsSubmitting || disableTwoFactorMutationIsPending}
              >
                {lang.get('msg.disableTwoFactorAuthentication')}
              </Button>
            </form>
          ) : (
            // Start 2FA Setup
            <Button onClick={startSetup} disabled={generateSecretMutationIsPenging}>
              {lang.get('msg.setupTwoFactorAuthentication')}
            </Button>
          )}
        </div>
      )}

      {/* QR Code Step */}
      {setupStep === TwoFactorSetupStep.QR_CODE && qrCodeData && (
        <div className="mt-5 max-w-lg">
          <h3 className="font-semibold mb-4">{lang.get('msg.twoFactorAuthenticationSetup')}</h3>

          <div className="mb-6">
            <p className="mb-4">{lang.get('msg.scanQRCode')}</p>
            <div className="flex bg-white p-4 rounded-md mb-4">
              <img src={qrCodeData.qrCodeUrl} alt="QR Code" className="w-64 h-64" />
            </div>
          </div>

          <form onSubmit={setupHandleSubmit(verifyAndEnable)} className="mb-4">
            <div className="flex flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative mb-1">
              <label style={{ color: currentTheme?.['primary-text'] }}>
                {lang.get('msg.enterCode')}
              </label>
              <Input
                {...setupRegister('code', {
                  onChange: () => {
                    if (error) setError('');
                  },
                })}
                type="text"
                maxLength={6}
                placeholder="000000"
                className={`text-center text-lg tracking-widest ${
                  errors.code ? 'border-red-600' : ''
                }`}
              />
              {errors.code?.message && (
                <p className="text-sm text-red-600">{lang.get(`msg.${errors.code?.message}`)}</p>
              )}
            </div>

            {error && <p className="text-sm text-red-600 font-semibold mb-4 mt-2">{error}</p>}

            <div className="flex gap-4 mt-4">
              <Button
                variant="default"
                type="submit"
                disabled={isSubmitting || !isValid || enableTwoFactorMutationIsPending}
              >
                {lang.get('msg.verifyCode')}
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => setSetupStep(TwoFactorSetupStep.INITIAL)}
              >
                {lang.get('msg.cancel')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Backup Codes Step */}
      {setupStep === TwoFactorSetupStep.BACKUP_CODES && backupCodes.length > 0 && (
        <div className="mt-5 max-w-lg">
          <h3 className="font-semibold mb-4">{lang.get('msg.saveBackupCodes')}</h3>
          <p className="mb-4">{lang.get('msg.backupCodesDescription')}</p>

          <div className="bg-gray-100 p-4 rounded-md mb-4 grid grid-cols-2 gap-2">
            {backupCodes.map((code) => (
              <div key={code} className="font-mono">
                {code}
              </div>
            ))}
          </div>

          <div className="flex gap-4 mb-4">
            <Button
              variant="outline"
              onClick={downloadBackupCodes}
              className="inline-flex items-center"
            >
              <IconDownload size={18} className="mr-2" />
              {lang.get('msg.downloadBackupCodes')}
            </Button>
            <Button
              variant="outline"
              onClick={copyBackupCodes}
              className="inline-flex items-center"
            >
              {copied ? (
                <IconCheck size={18} className="mr-2" />
              ) : (
                <IconCopy size={18} className="mr-2" />
              )}
              {lang.get('msg.copyBackupCodes')}
            </Button>
          </div>

          <Button variant="default" onClick={() => setSetupStep(TwoFactorSetupStep.INITIAL)}>
            {lang.get('msg.done')}
          </Button>
        </div>
      )}
    </>
  );
}

export default TwoFactorAuthForm;
